import React, { useEffect, useState } from "react";
import { Share2, Check, RefreshCw, QrCode, Download } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * Full public-share management card. Provides the portal URL, Copy +
 * WhatsApp share buttons, a scannable QR code (toggleable), and a Reset
 * button to rotate the token if it leaks. ADMIN-only — the underlying
 * API endpoints are role-gated. Rendered on the Settings page (full
 * management) and Competitions page (handy for sharing during the night).
 */
export default function PublicShareCard() {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");
  const [showQr, setShowQr] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/clubs/me/share");
        setToken(data.share_token);
      } catch (ex) {
        setErr(formatApiError(ex.response?.data?.detail) || ex.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const shareUrl = token
    ? `${window.location.origin}/portal?token=${token}`
    : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Public results link copied", { description: shareUrl, duration: 4000 });
    } catch (_) {
      toast.error("Couldn't copy — copy the link manually from the box.");
    }
  };

  const regenerate = async () => {
    if (
      !window.confirm(
        "Reset the public link?\n\n" +
          "Anyone who has the OLD link will immediately stop being able to see results. " +
          "You'll then need to send the new link to everyone."
      )
    )
      return;
    try {
      const { data } = await api.post("/clubs/me/share/regenerate");
      setToken(data.share_token);
      setCopied(false);
      toast.success("Public link reset — the old URL no longer works.", {
        description: "Send the new link to everyone who needs access.",
        duration: 6000,
      });
    } catch (ex) {
      const msg = formatApiError(ex.response?.data?.detail) || ex.message;
      setErr(msg);
      toast.error(`Reset failed: ${msg}`);
    }
  };

  const downloadQr = () => {
    // Find the rendered SVG, serialize it, draw onto a 720×720 canvas and
    // download as PNG so the admin can paste it into a slide deck.
    const svg = document.querySelector('[data-testid="public-share-qr-svg"]');
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const svg64 = btoa(unescape(encodeURIComponent(xml)));
    const img = new Image();
    img.onload = () => {
      const size = 720;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);
      const pad = 40;
      ctx.drawImage(img, pad, pad, size - 2 * pad, size - 2 * pad);
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "camera-club-portal-qr.png";
        a.click();
        URL.revokeObjectURL(url);
      });
    };
    img.src = `data:image/svg+xml;base64,${svg64}`;
  };

  return (
    <div className="ccs-card mb-6" data-testid="public-share-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="ccs-label mb-1 flex items-center gap-2">
            <Share2 size={12} /> Public results link
          </div>
          <div className="text-sm muted-text mb-3">
            Anyone with this link can view your club&rsquo;s competition results
            pages — no sign-in needed.
          </div>
          {loading ? (
            <div className="muted-text text-xs">Loading link…</div>
          ) : err ? (
            <div className="danger-text text-sm">{err}</div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  readOnly
                  value={shareUrl}
                  className="ccs-input mono text-xs flex-1 min-w-[260px]"
                  onFocus={(e) => e.target.select()}
                  data-testid="public-share-url"
                />
                <button
                  onClick={copy}
                  className="ccs-btn-ghost flex items-center gap-2 text-sm whitespace-nowrap"
                  data-testid="public-share-copy"
                >
                  {copied ? <Check size={14} /> : <Share2 size={14} />}
                  {copied ? "Copied!" : "Copy link"}
                </button>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(
                    `View our latest camera-club results: ${shareUrl}`
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                  className="ccs-btn-ghost flex items-center gap-2 text-sm whitespace-nowrap"
                  title="Send the public results link via WhatsApp"
                  data-testid="public-share-whatsapp"
                >
                  <WhatsAppIcon /> WhatsApp
                </a>
                <button
                  onClick={() => setShowQr((v) => !v)}
                  className="ccs-btn-ghost flex items-center gap-2 text-sm whitespace-nowrap"
                  title="Show a scannable QR code (great for slides and noticeboards)"
                  data-testid="public-share-qr-toggle"
                >
                  <QrCode size={14} /> {showQr ? "Hide QR" : "Show QR"}
                </button>
                <button
                  onClick={regenerate}
                  className="ccs-btn-ghost flex items-center gap-2 text-sm whitespace-nowrap"
                  title="Generate a new link and invalidate the old one"
                  data-testid="public-share-reset"
                >
                  <RefreshCw size={14} /> Reset link
                </button>
              </div>

              {showQr && (
                <div
                  className="mt-4 flex flex-col md:flex-row items-start gap-4 p-4 rounded-lg"
                  style={{ background: "#ffffff" }}
                  data-testid="public-share-qr-panel"
                >
                  <QRCodeSVG
                    value={shareUrl}
                    size={196}
                    level="M"
                    includeMargin={false}
                    data-testid="public-share-qr-svg"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-neutral-900 mb-1">
                      Scan to open the public results portal
                    </div>
                    <div className="text-xs text-neutral-600 mb-3">
                      Print this on a slide at the club night or pin it on the
                      noticeboard — anyone with a phone camera scans it once
                      and they&rsquo;re in.
                    </div>
                    <button
                      onClick={downloadQr}
                      className="ccs-btn-ghost flex items-center gap-2 text-sm whitespace-nowrap"
                      data-testid="public-share-qr-download"
                    >
                      <Download size={14} /> Download PNG
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// Inline brand-coloured WhatsApp glyph (lucide-react doesn't ship one).
// Exported so other pages can drop the same icon next to per-row share
// buttons without duplicating the SVG.
export function WhatsAppIcon({ size = 14 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="currentColor"
      style={{ color: "#25d366" }}
      aria-hidden="true"
    >
      <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 01-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 01-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.717.314-.43.473-1.245 1.36-1.245 2.79 0 1.07.86 2.405 1.36 3.08 1.06 1.43 2.24 2.535 3.853 3.45 1.547.886 2.79 1.075 3.293 1.075.802 0 2.535-.43 2.535-2.05 0-.115-.013-.215-.024-.31-.07-.262-.94-.444-1.59-.86z M16 6c5.523 0 10 4.477 10 10 0 1.81-.482 3.508-1.323 4.97L26 26l-5.13-1.342A9.95 9.95 0 0116 26C10.477 26 6 21.523 6 16S10.477 6 16 6m0-2C9.373 4 4 9.373 4 16c0 2.156.567 4.18 1.56 5.93L4 28l6.193-1.617A11.95 11.95 0 0016 28c6.627 0 12-5.373 12-12S22.627 4 16 4z" />
    </svg>
  );
}

